import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { setPageMeta, injectJsonLd, clearJsonLd, SITE_BASE } from '@/utils/seo';

const values = [
  {
    icon: 'ri-eye-line',
    title: '看得清来龙去脉',
    desc: '我们相信吃瓜的乐趣在于看清事件如何一步步演变，而不是只看某个孤立片段，所以坚持用时间轴呈现完整脉络。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '区分事实与猜测',
    desc: '把“已被证实的公开信息”和“尚未证实的传闻”清楚分开，帮读者在信息洪流里保持判断力。',
  },
  {
    icon: 'ri-history-line',
    title: '追更不中断',
    desc: '对仍在发酵的事件保持持续跟踪，有新进展就及时补节点、更新到最新，不让追更的人掉队。',
  },
  {
    icon: 'ri-bubble-chart-line',
    title: '尊重各方声音',
    desc: '时间轴尽量兼顾当事方、平台与网友的多元视角，减少情绪化标签，把话讲清楚比讲得狠更重要。',
  },
];

const voice = [
  {
    text: '以前追热点全靠零散刷帖，看了瓜迹的时间轴才把一件吵了很久的事从头到尾理清楚，爆发、回应、反转全在同一页，太省心了。',
    role: '追更用户 · 连续使用 3 个月',
  },
  {
    text: '最喜欢“最新进展”和“仍在发酵”的标记，一眼就知道哪件事还没完、值得继续关注，追更节奏感很强。',
    role: '栏目读者 · 常逛综艺与明星版块',
  },
  {
    text: '能清楚看到哪些节点标注了“来源待核实”，这点很加分。吃瓜的同时也慢慢养成了先看信源的习惯。',
    role: '关注品牌舆情的上班族',
  },
];

const orgLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '瓜迹',
  url: SITE_BASE,
  description: '按节点梳理每起热点事件经过与进展的吃瓜事件时间轴平台，持续更新最新动态。',
  email: 'hello@guaji.team',
  knowsAbout: ['吃瓜事件时间轴', '热点事件经过', '事件进展追踪'],
};

const aboutLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于瓜迹',
  url: `${SITE_BASE}/about`,
  mainEntity: orgLd,
};

export default function About() {
  useEffect(() => {
    setPageMeta({
      title: '关于瓜迹 - 吃瓜事件时间轴的梳理与更新原则',
      description:
        '了解瓜迹：一个按节点梳理吃瓜事件经过与进展的时间轴平台，了解我们的内容梳理原则、追更流程、用户口碑与联系方式。',
      keywords: '瓜迹,吃瓜事件时间轴,关于我们,事件梳理原则,热点追踪',
      path: '/about',
    });
    const c1 = injectJsonLd(orgLd);
    const c2 = injectJsonLd(aboutLd);
    return () => {
      clearJsonLd();
      c1();
      c2();
    };
  }, []);

  return (
    <Layout>
      {/* 品牌页头 */}
      <section className="border-b border-background-300/60 bg-gradient-to-b from-primary-50 to-background-50">
        <div className="mx-auto max-w-5xl px-4 py-12 text-center md:px-6 md:py-16">
          <nav aria-label="面包屑" className="flex items-center justify-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <span className="mx-auto mt-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-500 font-heading text-3xl text-background-50">瓜</span>
          <h1 className="mt-5 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">关于瓜迹</h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            瓜迹是一个专注「吃瓜事件时间轴」的内容平台。我们不追着标题跑，而是帮你在铺天盖地的讨论里，
            把每件热点事件从爆发到后续的经过与进展，按节点一页看清楚。
          </p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <a href="#principles" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
              查看梳理原则 <i className="ri-arrow-down-line" />
            </a>
            <Link to="/events" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700">
              去看看事件时间轴
            </Link>
          </div>
        </div>
      </section>

      {/* 定位 */}
      <section className="mx-auto max-w-5xl px-4 py-12 md:px-6">
        <div className="grid gap-8 md:grid-cols-3 md:items-center">
          <div className="md:col-span-1">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-accent-700"><i className="ri-compass-line" /> 我们想解决什么</span>
            <h2 className="mt-2 font-heading text-2xl font-semibold leading-snug text-foreground-950">吃瓜一时爽，追清才不慌</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground-600 md:col-span-2">
            热点事件往往是碎片化的：先看到截图、再刷到回应、隔几天又反转。瓜迹把这些碎片按时间串成一条线，
            让每个人都能用最短时间了解「发生了什么、官方怎么说、进展到哪一步、后续还会怎么变」。
            我们希望这里成为你追更吃瓜动态时的「索引」，事件脉络清清楚楚，追更不掉队。
          </p>
        </div>
      </section>

      {/* 内容梳理原则 */}
      <section id="principles" className="scroll-mt-24 border-y border-background-300/60 bg-background-100/70 py-12 md:py-16">
        <div className="mx-auto max-w-5xl px-4 md:px-6">
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-primary-600"><i className="ri-check-double-line" /> 内容原则</span>
            <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">我们如何保证每条时间轴可信</h2>
          </div>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {[
              { icon: 'ri-list-check-3', t: '多源交叉核验', d: '官方通稿、当事人声明、权威媒体优先，多重信源一致才纳入节点，避免单方面说法以讹传讹。' },
              { icon: 'ri-time-line', t: '节点按事实标注', d: '每个节点写清“谁、何时、做了什么”，把进展、回应与传闻严格区分，标注阶段标签便于阅读。' },
              { icon: 'ri-error-warning-line', t: '不实传闻明确标注', d: '仅靠匿名爆料或无法核实的信息，会如实标注“待核实”，绝不把猜测包装成既定事实。' },
              { icon: 'ri-refresh-line', t: '持续修正与补全', d: '收到新线索或发现旧节点有误时，会及时更正并保留更新时间，让时间轴始终贴近最新进展。' },
            ].map((p) => (
              <div key={p.t} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-700"><i className={p.icon} /></span>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950">{p.t}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 网站价值 */}
      <section className="mx-auto max-w-5xl px-4 py-12 md:px-6 md:py-16">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-primary-600"><i className="ri-sparkling-2-line" /> 平台价值</span>
          <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">我们相信吃瓜可以更有条理</h2>
        </div>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <div key={v.title} className="group relative overflow-hidden rounded-xl border border-background-200 bg-background-50 p-5">
              <span className="absolute right-4 top-3 font-heading text-4xl text-background-200 transition-colors group-hover:text-primary-200 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-lg text-background-50"><i className={v.icon} /></span>
              <h3 className="mt-4 text-base font-semibold text-foreground-950">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 追更流程 (method 锚点) */}
      <section id="method" className="scroll-mt-24 border-y border-background-300/60 bg-accent-100/40 py-12 md:py-16">
        <div className="mx-auto max-w-5xl px-4 md:px-6">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-accent-700"><i className="ri-stack-line" /> 追更流程</span>
              <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">一条时间轴是如何诞生的</h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-foreground-600">从捕捉热点到持续更新，瓜迹用统一流程保证内容质量与更新速度。</p>
          </div>
          <ol className="mt-8 grid gap-5 md:grid-cols-4">
            {[
              { n: '捕捉苗头', d: '发现潜在热点，判断传播潜力与是否需要长期追踪。', icon: 'ri-radar-line' },
              { n: '核验信源', d: '交叉比对通稿、当事人回应与权威渠道。', icon: 'ri-focus-3-line' },
              { n: '拉通时间线', d: '拆解为“爆发—发酵—回应—后续”等节点并串联。', icon: 'ri-node-tree' },
              { n: '持续更新', d: '新进展及时补充节点，保持追更不断线。', icon: 'ri-loop-left-line' },
            ].map((s, i) => (
              <li key={s.n} className="relative rounded-xl border border-background-200 bg-background-50 p-5">
                <span className="absolute -top-3 left-5 flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 text-sm font-bold text-foreground-950 tabular-nums">{i + 1}</span>
                <i className={`mt-1 text-2xl text-accent-600 ${s.icon}`} />
                <h3 className="mt-3 text-base font-semibold text-foreground-950">{s.n}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 用户口碑 */}
      <section className="mx-auto max-w-5xl px-4 py-12 md:px-6 md:py-16">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-primary-600"><i className="ri-heart-line" /> 来自追更用户</span>
          <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">大家眼里的瓜迹</h2>
        </div>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {voice.map((v) => (
            <figure key={v.role} className="flex flex-col rounded-xl border border-background-200 bg-background-50 p-6">
              <div className="flex gap-0.5 text-accent-500">
                {Array.from({ length: 5 }).map((_, i) => <i key={i} className="ri-star-fill text-sm" />)}
              </div>
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">“{v.text}”</blockquote>
              <figcaption className="mt-4 border-t border-background-200 pt-3 text-xs text-foreground-500">{v.role}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* 联系我们 */}
      <section id="contact" className="scroll-mt-24 bg-primary-600">
        <div className="mx-auto max-w-5xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid items-center gap-8 md:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-accent-300"><i className="ri-mail-send-line" /> 联系我们</span>
              <h2 className="mt-2 font-heading text-2xl font-semibold text-background-50 md:text-3xl">一起把时间轴补得更完整</h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-primary-100">
                发现一件值得追踪的新热事？掌握公开可查的最新进展？或觉得某条时间轴有疏漏？欢迎联系我们，
                编辑核实后会及时补充或更正节点。
              </p>
              <ul className="mt-5 space-y-2.5 text-sm text-primary-50">
                <li className="flex items-center gap-2.5"><i className="ri-mail-line text-accent-300" /> hello@guaji.team</li>
                <li className="flex items-center gap-2.5"><i className="ri-refresh-line text-accent-300" /> 事件时间轴每周高频更新</li>
                <li className="flex items-center gap-2.5"><i className="ri-time-line text-accent-300" /> 工作日与热点期人工值守</li>
              </ul>
            </div>
            <div className="rounded-xl bg-background-50/95 p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">反馈前的小提醒</h3>
              <ul className="mt-3 space-y-2 text-sm text-foreground-600">
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-accent-600" />尽量提供可公开查证的来源链接，我们会优先采纳。</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-accent-600" />涉及隐私或未经证实的传闻，恕难收录进时间轴。</li>
                <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-accent-600" />线索较多时，请用“事件名称 + 时间 + 来源”的方式简要描述。</li>
              </ul>
              <a href="mailto:hello@guaji.team?subject=反馈新线索" className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">
                <i className="ri-send-plane-line" /> 写邮件给瓜迹
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}